import * as React from "react";
import { Platform } from "react-native";
import { DefaultTheme, NavigationContainer } from "@react-navigation/native";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { AppThemed } from "./Pages/Main";
import RankingScreen from "./screens/RankingScreen";
import LoginScreen from "./screens/LoginScreen";
import AuthGateScreen from "./screens/AuthGateScreen";
import SettingsScreen from "./screens/SettingsScreen";
import InvitesScreen from "./screens/InvitesScreen";
import MessagesScreen from "./screens/MessagesScreen";

import GameRulesScreen from "./screens/GameRulesScreen";
import RegisterScreen from "./screens/RegisterScreen";
import SearchScreen from "./screens/SearchScreen";
import PersonalDataScreen from "./screens/PersonalDataScreen";
import {
  LobbyScreen,
  MainScreen,
  RoomScreen,
  TournamentScreen,
  TournamentsScreen,
  ActiveTournamentScreen,
} from "./screens/MainScreens";
import ProfileScreen from "./screens/ProfileScreen";
import GameScreen from "./screens/GameScreen";
import BuyPackagesScreen from "./screens/BuyPackagesScreen";

import MyTablesScreen from "./screens/MyTablesScreen";
import JoinTournamentsScreen from "./screens/JoinTournamentScreen";
import { navigationRef } from "../utils/helpers/navigationService";
import NotificationsListener from "./navigation/NotificationsListener";
import VipTablesScreen from "./screens/VipTablesScreen";
import HelpScreen from "./screens/HelpScreen";
import TutorialScreen from "./screens/TutorialScreen";
import PaymentCallbackScreen from "./Components/PaymentCallback/EPayCallback";
import PaymentSuccessScreen from "./screens/PaymentSuccessScreen";
import PaymentErrorScreen from "./screens/PaymentErrorScreen";
import { firebaseAnalytics } from "../services/firebase/firebase.service";
import { useRef } from "react";
export type RootStackParamList = {
  Main: undefined;
  Ranking: undefined;
  Lobby: undefined;
  Login: undefined;
  AuthGate: undefined;
  Settings: undefined;
  Invites: undefined;
  Messages: undefined;
  Profile: undefined;
  GameRules: undefined;
  MyTables: undefined;
  Register: undefined;
  VipTables: undefined;
  Search: undefined;
  PersonalData: undefined;
  Help: undefined;
  JoinTournaments:
    | {
        tournamentId: string;
        entryLevel?: number;
      }
    | undefined;
  BuyPackages: { activeTab?: "chips" | "vip" };
  Tournaments: undefined;
  Rooms: undefined;
  Game: {
    tableId?: number | string;
    legacyRoomId?: number;
    roomSlug?: string;
    isTutorial?: boolean;
    tableName?: string;
    tableStringId?: string;
    isTournamentRoom?: boolean;
    timestamp?: number;
  };
  Tutorial: {
    tableId?: number | string;
    legacyRoomId?: number;
    roomSlug?: string;
    isTutorial?: boolean;
    tableName?: string;
    tableStringId?: string;
    isTournamentRoom?: boolean;
    timestamp?: number;
  };
  LobbyTables: undefined;
  Room: { roomSlug: string; roomId: string };
  Tournament: { tournamentId: string; roomId: string };
  PaymentCallback: undefined;
  PaymentSuccess: undefined;
  PaymentError: undefined;
  ActiveTournament: { roomId: string };
};

const Stack = createNativeStackNavigator<RootStackParamList>();

const linking = {
  prefixes: [
    // Native deep link scheme; web origin not needed here
    "svara://",
    "/",
  ],
  config: {
    screens: {
      Main: "/",
      Lobby: "lobby",
      Tournaments: "tournaments",
      Tournament: "tournament/:tournamentId/room/:roomId",
      ActiveTournament: "active-tournament/:roomId",
      Room: "room/:roomSlug",
      Ranking: "ranking",
      Login: "login",
      Settings: "settings",
      Invites: "invites",
      Messages: "messages",
      Profile: "profile",
      GameRules: "rules",
      Register: "register",
      Search: "search",
      PersonalData: "personal-data",
      Rooms: "rooms",
      Game: "game",
      LobbyTables: "lobby-tables",
    },
  },
};

// Only use URL linking on native so web doesn't change URL and trigger reloads/404s
const linkingEnabled = Platform.OS !== "web" ? linking : undefined;

export default function RootNavigator() {
  const routeNameRef = useRef<string>();
  return (
    <NavigationContainer
      linking={linkingEnabled}
      ref={navigationRef}
      onReady={() => {
        routeNameRef.current = navigationRef.current?.getCurrentRoute()?.name;
        console.log("🧭 [NAV] ready");
      }}
      onStateChange={async () => {
        const previousRouteName = routeNameRef.current;
        const currentRouteName = navigationRef.current?.getCurrentRoute()?.name;

        if (previousRouteName !== currentRouteName && currentRouteName) {
          // 🔥 Track screen view on navigation
          if (
            firebaseAnalytics &&
            typeof firebaseAnalytics.logScreenView === "function"
          ) {
            try {
              await firebaseAnalytics.logScreenView(currentRouteName);
            } catch (error) {
              console.warn("[Navigator] Failed to log screen view:", error);
            }
          }
        }

        // Save the current route name for next time
        routeNameRef.current = currentRouteName;
      }}
    >
      <NotificationsListener />
      <Stack.Navigator
        screenOptions={{
          headerShown: false,
          // animation: "none",
        }}
      >
        <Stack.Screen
          name="Main"
          component={MainScreen}
          options={{ animation: "fade" }}
        />
        <Stack.Screen
          name="Lobby"
          component={LobbyScreen}
          options={{ animation: "fade" }}
        />
        <Stack.Screen
          name="Tournaments"
          component={TournamentsScreen}
          options={{ animation: "fade" }}
        />
        <Stack.Screen
          name="Tournament"
          component={TournamentScreen}
          // initialParams={{ tournamentId: "sofia" }}
          options={{ animation: "fade" }}
        />
        <Stack.Screen
          name="Room"
          component={RoomScreen}
          initialParams={{ roomSlug: "sofia" }}
          options={{ animation: "fade" }}
        />
        <Stack.Screen
          name="ActiveTournament"
          component={ActiveTournamentScreen}
          options={{ animation: "fade" }}
        />
        <Stack.Screen
          name="Game"
          component={GameScreen}
          options={{
            headerShown: false,
            presentation: "fullScreenModal",
            gestureEnabled: false,
          }}
        />

        <Stack.Screen
          name="Tutorial"
          component={TutorialScreen}
          options={{
            headerShown: false,
            presentation: "fullScreenModal",
            gestureEnabled: true, // Enable swipe back gesture
          }}
        />

        <Stack.Screen
          name="Ranking"
          component={RankingScreen}
          options={{
            presentation: "transparentModal",
          }}
        />
        <Stack.Screen
          name="VipTables"
          component={VipTablesScreen}
          options={{
            presentation: "transparentModal",
          }}
        />
        <Stack.Screen
          name="JoinTournaments"
          component={JoinTournamentsScreen}
          options={{
            presentation: "transparentModal",
          }}
        />
        <Stack.Screen
          name="Login"
          component={LoginScreen}
          options={{
            presentation: "transparentModal",
          }}
        />
        <Stack.Screen
          name="AuthGate"
          component={AuthGateScreen}
          options={{
            presentation: "transparentModal",
          }}
        />
        <Stack.Screen
          name="Settings"
          component={SettingsScreen}
          options={{
            presentation: "transparentModal",
          }}
        />
        <Stack.Screen
          name="Invites"
          component={InvitesScreen}
          options={{
            presentation: "transparentModal",
          }}
        />
        <Stack.Screen
          name="Messages"
          component={MessagesScreen}
          options={{
            presentation: "transparentModal",
          }}
        />
        <Stack.Screen
          name="Help"
          component={HelpScreen}
          options={{
            presentation: "transparentModal",
          }}
        />
        <Stack.Screen
          name="Profile"
          component={ProfileScreen}
          options={{
            presentation: "transparentModal",
          }}
        />
        <Stack.Screen
          name="BuyPackages"
          component={BuyPackagesScreen}
          options={{
            presentation: "transparentModal",
          }}
        />
        <Stack.Screen
          name="GameRules"
          component={GameRulesScreen}
          options={{
            presentation: "transparentModal",
          }}
        />
        <Stack.Screen
          name="MyTables"
          component={MyTablesScreen}
          options={{
            presentation: "transparentModal",
          }}
        />
        {/* <Stack.Screen
          name="Register"
          component={RegisterScreen}
          options={{
            presentation: "transparentModal",
          }}
        /> */}
        <Stack.Screen
          name="Search"
          component={SearchScreen}
          options={{
            presentation: "transparentModal",
          }}
        />
        <Stack.Screen
          name="PersonalData"
          component={PersonalDataScreen}
          options={{
            presentation: "transparentModal",
          }}
        />

        <Stack.Screen
          name="PaymentCallback"
          component={PaymentCallbackScreen}
          options={{
            headerShown: false,
            presentation: "fullScreenModal",
          }}
        />
        <Stack.Screen
          name="PaymentSuccess"
          component={PaymentSuccessScreen}
          options={{
            headerShown: false,
            presentation: "fullScreenModal",
          }}
        />
        <Stack.Screen
          name="PaymentError"
          component={PaymentErrorScreen}
          options={{
            headerShown: false,
            presentation: "fullScreenModal",
          }}
        />
      </Stack.Navigator>
    </NavigationContainer>
  );
}
